import type { WizardData } from "@/pages/CreateCard";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PenLine, Wallet, ShoppingBag } from "lucide-react";

interface Props {
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
}

const StepPurpose = ({ data, updateData }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const selectSignOnly = () => {
    updateData({
      purposeSelected: true,
      collectionEnabled: false,
      type: "card_only",
    });
  };

  const selectSignAndChipIn = () => {
    updateData({
      purposeSelected: true,
      collectionEnabled: true,
      type: "card_collection",
    });
  };

  // Picking "Pool for a product" bails out of the money/sign wizard
  // entirely and sends the user into the dedicated product-pool flow
  // at /create/product. That flow has its own step indicator and
  // collects a different set of fields (product URL, shipping, etc.)
  // so it doesn't share wizard state with this component.
  const selectPoolForProduct = () => {
    navigate("/create/product");
  };

  const isSignOnly = data.purposeSelected && !data.collectionEnabled;
  const isSignAndChipIn = data.purposeSelected && data.collectionEnabled;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground font-serif mb-1">
          {t("wizard.purpose.title", { defaultValue: "How will your group join in?" })}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t("wizard.purpose.subtitle", {
            defaultValue: "Pick how your group will participate. You can change this later.",
          })}
        </p>
      </div>

      <div className="grid gap-3">
        <button
          type="button"
          onClick={selectSignOnly}
          className={`w-full text-start rounded-xl border p-5 transition-all ${
            isSignOnly
              ? "border-primary bg-primary/[0.05]"
              : "border-border hover:border-foreground/30"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <PenLine size={18} className="text-foreground" />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-foreground">
                {t("wizard.purpose.signOnly", { defaultValue: "Sign only" })}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("wizard.purpose.signOnlyDesc", {
                  defaultValue: "Collect messages and signatures from the group.",
                })}
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={selectSignAndChipIn}
          className={`w-full text-start rounded-xl border p-5 transition-all ${
            isSignAndChipIn
              ? "border-primary bg-primary/[0.05]"
              : "border-border hover:border-foreground/30"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Wallet size={18} className="text-foreground" />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-foreground">
                {t("wizard.purpose.signAndChipIn", { defaultValue: "Sign and chip in" })}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("wizard.purpose.signAndChipInDesc", {
                  defaultValue: "Collect messages and pool money for a group gift.",
                })}
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={selectPoolForProduct}
          className="relative w-full text-start rounded-xl border border-border p-5 transition-all hover:border-foreground/30"
        >
          <span className="absolute top-2 end-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary text-primary-foreground">
            {t("wizard.purpose.newBadge", { defaultValue: "New" })}
          </span>
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <ShoppingBag size={18} className="text-foreground" />
            </div>
            <div className="flex-1 pe-10">
              <div className="text-base font-semibold text-foreground">
                {t("wizard.purpose.poolForProduct", { defaultValue: "Pool for a product" })}
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                {t("wizard.purpose.poolForProductDesc", {
                  defaultValue: "Chip in together for a specific item from any store.",
                })}
              </div>
            </div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default StepPurpose;
